import cn from 'classnames';
import capitalize from 'lodash-es/capitalize';
import { FC } from 'react';
import { match } from 'ts-pattern';

import { SanitizeMode, getCleanInput, isBuilderStringPresent } from '../text';
import { BuilderString, LinkTarget } from '../types';
import style from './Media.module.scss';
import { Video, Settings as VideoSettings } from './Video/Video';
import { YouTubeVideo, YoutubeSettings } from './YouTubeVideo/YouTubeVideo';

export enum MediaType {
  NONE = 'none',
  VIDEO = 'video',
  IMAGE = 'image',
  YOUTUBE = 'youtube',
}

export interface MediaInput {
  mediaType: MediaType;
  mediaCaption?: string;
  youtube?: YoutubeSettings;
  video?: VideoSettings;
  image?: string;
  imageLink?: string;
  imageLinkTarget?: LinkTarget;
}

interface MediaCaptionProps {
  locale: string;
  caption?: BuilderString;
}
export const MediaCaption: FC<MediaCaptionProps> = ({ locale, caption }) => {
  if (!isBuilderStringPresent(caption, locale)) return null;

  return (
    <div
      data-testid="media-caption"
      className="u-m-t-s u-typography-body-s u-color-tertiary"
    >
      {getCleanInput(locale, SanitizeMode.SINGLE_LINE_TEXT_ONLY, caption)}
    </div>
  );
};

interface Props extends MediaInput {
  locale: string;
  className?: string;
  containerClassName?: string;
}
export const Media: FC<Props> = ({
  mediaType,
  mediaCaption,
  video,
  image,
  imageLink,
  imageLinkTarget,
  youtube,
  locale,
  className,
  containerClassName,
}) =>
  match(mediaType)
    .with(MediaType.IMAGE, () => {
      if (!isBuilderStringPresent(image, locale)) return null;

      const alt = getCleanInput(
        locale,
        SanitizeMode.MULTIPLE_LINE,
        mediaCaption
      );
      const src = getCleanInput(
        locale,
        SanitizeMode.SINGLE_LINE_TEXT_ONLY,
        image
      );
      const link = getCleanInput(
        locale,
        SanitizeMode.SINGLE_LINE_TEXT_ONLY,
        imageLink
      );

      return (
        <div
          data-testid="media"
          className={cn('tw:flex', 'tw:justify-end', className)}
        >
          <div
            className={cn(
              style.container,
              'tw:justify-center',
              containerClassName
            )}
          >
            {imageLink ? (
              <a
                className="tw:flex"
                href={link}
                target={imageLinkTarget ?? '_blank'}
              >
                <img
                  data-testid="media-image"
                  className={style.image}
                  alt={alt}
                  src={src}
                />
              </a>
            ) : (
              <img
                data-testid="media-image"
                className={style.image}
                alt={alt}
                src={src}
              />
            )}

            <MediaCaption locale={locale} caption={mediaCaption} />
          </div>
        </div>
      );
    })
    .with(MediaType.VIDEO, () => {
      if (!video?.source) return null;
      if (!isBuilderStringPresent(video.source, locale)) return null;

      return (
        <div
          data-testid="media"
          className={cn(
            'tw:flex',
            'tw:justify-center',
            'tw:items-start',
            style.container,
            className
          )}
        >
          <Video
            className={cn(style.container, containerClassName)}
            locale={locale}
            {...video}
          />
          <MediaCaption locale={locale} caption={mediaCaption} />
        </div>
      );
    })
    .with(MediaType.YOUTUBE, () => {
      if (!youtube) return null;

      return (
        <div
          data-testid="media"
          className={cn('tw:flex', 'tw:justify-end', className)}
        >
          <div
            className={cn(
              style.container,
              'tw:justify-center',
              containerClassName
            )}
          >
            <YouTubeVideo
              locale={locale}
              mediaCaption={mediaCaption}
              {...youtube}
            />
            <MediaCaption locale={locale} caption={mediaCaption} />
          </div>
        </div>
      );
    })
    .otherwise(() => null);

interface InputParams {
  showMobileSource: boolean;
  useCatawikiPlainEditor?: boolean;
}

export const mediaInput = ({
  showMobileSource = false,
  useCatawikiPlainEditor = true,
}: InputParams) => [
  {
    name: 'mediaType',
    friendlyName: 'Media Type',
    type: 'string',
    defaultValue: 'none',
    required: true,
    enum: Object.values(MediaType).map((name) => ({
      label: capitalize(name),
      value: name,
    })),
  },
  {
    name: 'mediaCaption',
    friendlyName: 'Media Caption',
    type: useCatawikiPlainEditor
      ? 'CatawikiPlainEditor'
      : 'CatawikiPlainEditorWithoutBuiltInTranslation',
    showIf: `options.get('mediaType') !== 'none'`,
  },
  {
    name: 'image',
    friendlyName: 'Image',
    type: 'file',
    allowedFileTypes: ['jpeg', 'png'],
    showIf: `options.get('mediaType') === 'image'`,
  },
  {
    name: 'imageLink',
    friendlyName: 'Image link',
    helperText: 'Link that opens when click on the image',
    type: useCatawikiPlainEditor
      ? 'CatawikiPlainEditor'
      : 'CatawikiPlainEditorWithoutBuiltInTranslation',
    showIf: `options.get('mediaType') === 'image'`,
  },
  {
    name: 'imageLinkTarget',
    friendlyName: 'Open link target',
    type: 'string',
    enum: Object.keys(LinkTarget).map((name, index) => ({
      label: capitalize(name).split('_').join(' '),
      value: Object.values(LinkTarget)[index],
    })),
    defaultValue: '_blank',
    showIf: `options.get('mediaType') === 'image'`,
  },
  {
    name: 'video',
    friendlyName: 'Video',
    type: 'object',
    showIf: `options.get('mediaType') === 'video'`,
    subFields: [
      {
        name: 'autoPlay',
        friendlyName: 'AutoPlay video',
        type: 'boolean',
        defaultValue: false,
      },
      {
        name: 'poster',
        friendlyName: 'Poster image',
        type: 'file',
        allowedFileTypes: ['jpeg', 'png'],
        localized: true,
      },
      {
        name: 'source',
        friendlyName: 'Video File',
        type: 'file',
        allowedFileTypes: ['mp4'],
        localized: true,
      },
      showMobileSource
        ? {
            name: 'mobileSource',
            friendlyName: 'Mobile Video File',
            type: 'file',
            allowedFileTypes: ['mp4'],
            localized: true,
          }
        : null,
    ].filter((i) => i),
  },
  {
    name: 'youtube',
    friendlyName: 'YouTube',
    type: 'object',
    showIf: `options.get('mediaType') === 'youtube'`,
    subFields: [
      {
        name: 'poster',
        friendlyName: 'Poster image',
        type: 'file',
        allowedFileTypes: ['jpeg', 'png'],
        localized: true,
      },
      {
        name: 'url',
        friendlyName: 'YouTube Shared URL',
        type: useCatawikiPlainEditor
          ? 'CatawikiPlainEditor'
          : 'CatawikiPlainEditorWithoutBuiltInTranslation',
      },
      {
        name: 'disabledDialog',
        friendlyName: 'Show YouTube without dialog',
        type: 'boolean',
        defaultValue: false,
      },
    ],
  },
];

export interface FlatMediaInput {
  mediaType: MediaType;
  mediaCaption?: string;
  imageUrl?: string;
  imageLink?: string;
  imageLinkTarget?: LinkTarget;
  videoUrl?: string;
  videoAutoPlay?: boolean;
  youtubeUrl?: string;
  youtubeDisabledDialog?: boolean;
}

interface FlatMediaInputParams {
  useCatawikiPlainEditor?: boolean;
}

// This flat media input is using in sublist that you can't put
// another level of sublist inside e.g. in ContentImage component
export const flatMediaInput = ({
  useCatawikiPlainEditor = true,
}: FlatMediaInputParams) => [
  {
    name: 'mediaType',
    friendlyName: 'Media Type',
    type: 'string',
    defaultValue: 'none',
    required: true,
    enum: Object.values(MediaType).map((name) => ({
      label: capitalize(name),
      value: name,
    })),
  },
  {
    name: 'mediaCaption',
    friendlyName: 'Media Caption',
    type: useCatawikiPlainEditor
      ? 'CatawikiPlainEditor'
      : 'CatawikiPlainEditorWithoutBuiltInTranslation',
    showIf: `options.get('mediaType') !== 'none'`,
  },
  {
    name: 'imageUrl',
    friendlyName: 'Image or Video (and YouTube) poster image',
    type: 'file',
    allowedFileTypes: ['jpeg', 'png'],
    localized: true,
  },
  {
    name: 'imageLink',
    friendlyName: 'Link to open when click on image',
    type: useCatawikiPlainEditor
      ? 'CatawikiPlainEditor'
      : 'CatawikiPlainEditorWithoutBuiltInTranslation',
    showIf: `options.get('mediaType') === 'image'`,
  },
  {
    name: 'imageLinkTarget',
    friendlyName: 'Open link target',
    type: 'string',
    enum: Object.keys(LinkTarget).map((name, index) => ({
      label: capitalize(name).split('_').join(' '),
      value: Object.values(LinkTarget)[index],
    })),
    defaultValue: '_blank',
    showIf: `options.get('mediaType') === 'image'`,
  },
  {
    name: 'videoUrl',
    friendlyName: 'Video source',
    type: 'file',
    localized: true,
    allowedFileTypes: ['mp4'],
    showIf: `options.get('mediaType') === 'video'`,
  },
  {
    name: 'videoAutoPlay',
    friendlyName: 'AutoPlay video',
    type: 'boolean',
    defaultValue: false,
    showIf: `options.get('mediaType') === 'video'`,
  },
  {
    name: 'youtubeUrl',
    friendlyName: 'YouTubeUrl',
    type: useCatawikiPlainEditor
      ? 'CatawikiPlainEditor'
      : 'CatawikiPlainEditorWithoutBuiltInTranslation',
    showIf: `options.get('mediaType') === 'youtube'`,
  },
  {
    name: 'youtubeDisabledDialog',
    friendlyName: 'Show YouTube without dialog',
    type: 'boolean',
    defaultValue: false,
    showIf: `options.get('mediaType') === 'youtube'`,
  },
];
